import { useState, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import {
  type OnboardingProgress,
  type SiteType,
  ONBOARDING_STEPS,
  completeStep,
  skipStep,
  getCompletionPercentage,
  getStepDefsForType,
  isOnboardingComplete,
  createInitialProgress,
} from "@/lib/onboarding";
import { WelcomeStep } from "./steps/welcome";
import { BusinessInfoStep } from "./steps/business-info";
import { LogoUploadStep } from "./steps/logo-upload";
import { PaymentSetupStep } from "./steps/payment-setup";
import { ShippingConfigStep } from "./steps/shipping-config";
import { FirstProductStep } from "./steps/first-product";
import { ServicesSetupStep } from "./steps/services-setup";
import { WorkHoursStep } from "./steps/work-hours";
import { ContactFormStep } from "./steps/contact-form";
import { WhatsAppConnectStep } from "./steps/whatsapp-connect";
import { ReviewPublishStep } from "./steps/review-publish";

/**
 * Onboarding wizard container.
 * Loads progress from API, renders current step, handles navigation.
 * Mobile-first, RTL Hebrew.
 */
export function OnboardingPage(): ReactNode {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);

  // Load onboarding progress
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const res = await fetch("/api/onboarding/progress", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          // No onboarding found — redirect to dashboard
          if (!cancelled) navigate("/", { replace: true });
          return;
        }
        const data = (await res.json()) as { data: OnboardingProgress | null };
        if (cancelled) return;
        if (data.data === null || isOnboardingComplete(data.data)) {
          navigate("/", { replace: true });
          return;
        }
        setProgress(data.data);
      } catch {
        if (!cancelled) navigate("/", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [accessToken, navigate]);

  const stepDefs = useMemo(
    () => (progress !== null ? getStepDefsForType(progress.siteType) : []),
    [progress],
  );

  const currentStepIndex = useMemo(
    () => (progress !== null ? stepDefs.findIndex((d) => d.id === progress.currentStep) : 0),
    [progress, stepDefs],
  );

  const percentage = useMemo(
    () => (progress !== null ? getCompletionPercentage(progress) : 0),
    [progress],
  );

  const currentStepDef = useMemo(
    () => stepDefs[currentStepIndex],
    [stepDefs, currentStepIndex],
  );

  const handleComplete = useCallback(
    async (stepId: string) => {
      if (progress === null) return;
      const updated = completeStep(progress, stepId);
      setProgress(updated);
      // Persist to API
      try {
        await fetch("/api/onboarding/complete-step", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stepId }),
        });
      } catch {
        // UI already updated optimistically
      }
      if (isOnboardingComplete(updated)) {
        // Give a moment to show completion
        setTimeout(() => navigate("/", { replace: true }), 3000);
      }
    },
    [progress, accessToken, navigate],
  );

  const handleSkip = useCallback(
    async (stepId: string) => {
      if (progress === null) return;
      const updated = skipStep(progress, stepId);
      setProgress(updated);
      try {
        await fetch("/api/onboarding/skip-step", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ stepId }),
        });
      } catch {
        // Optimistic
      }
    },
    [progress, accessToken],
  );

  const goToPreviousStep = useCallback(() => {
    if (progress === null || currentStepIndex <= 0) return;
    const prevStep = stepDefs[currentStepIndex - 1];
    if (prevStep !== undefined) {
      setProgress({ ...progress, currentStep: prevStep.id });
    }
  }, [progress, currentStepIndex, stepDefs]);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          <p className="text-sm text-muted-foreground">{"\u05D8\u05D5\u05E2\u05DF..."}</p>
        </div>
      </div>
    );
  }

  if (progress === null) return null;

  return (
    <div dir="rtl" lang="he" className="mx-auto flex min-h-dvh max-w-lg flex-col">
      {/* Progress bar */}
      <div className="sticky top-0 z-10 border-b border-border/60 bg-background/95 backdrop-blur-sm px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground">
            {`\u05E6\u05E2\u05D3 ${currentStepIndex + 1} \u05DE\u05EA\u05D5\u05DA ${stepDefs.length}`}
          </span>
          <span className="text-sm font-bold text-primary">{percentage}%</span>
        </div>

        {/* Main progress bar */}
        <div className="h-2 overflow-hidden rounded-full bg-muted/60">
          <div
            className="h-full rounded-full bg-gradient-to-l from-primary to-primary/70 transition-all duration-500 ease-out shadow-sm"
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Step dot indicators */}
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-0.5">
          {stepDefs.map((def, i) => {
            const step = progress.steps[i];
            const isCurrent = i === currentStepIndex;
            const isDone = step !== undefined && (step.completed || step.skipped);
            return (
              <div
                key={def.id}
                className={`transition-all duration-300 rounded-full ${
                  isDone
                    ? "h-1.5 flex-1 bg-primary"
                    : isCurrent
                      ? "h-1.5 flex-[2] bg-primary/60"
                      : "h-1.5 flex-1 bg-muted"
                }`}
              />
            );
          })}
        </div>

        {/* Current step name */}
        {currentStepDef && (
          <p className="mt-2 text-xs text-muted-foreground truncate">
            {currentStepDef.label}
          </p>
        )}
      </div>

      {/* Back button */}
      {currentStepIndex > 0 && progress.currentStep !== "review_publish" && (
        <button
          type="button"
          className="flex min-h-[44px] items-center gap-1.5 px-4 pt-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          onClick={goToPreviousStep}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
          {"\u05D4\u05E7\u05D5\u05D3\u05DD"}
        </button>
      )}

      {/* Current step content */}
      <div className="flex-1">
        {renderStep(
          progress,
          handleComplete,
          handleSkip,
        )}
      </div>
    </div>
  );
}

function renderStep(
  progress: OnboardingProgress,
  onComplete: (stepId: string) => void,
  onSkip: (stepId: string) => void,
): ReactNode {
  const stepId = progress.currentStep;
  const stepDefs = getStepDefsForType(progress.siteType);

  switch (stepId) {
    case "welcome":
      return (
        <WelcomeStep
          greetingText={progress.greetingText ?? ""}
          steps={stepDefs}
          onNext={() => onComplete("welcome")}
        />
      );

    case "business_info":
      return (
        <BusinessInfoStep
          initialName=""
          onComplete={() => onComplete("business_info")}
        />
      );

    case "logo_upload":
      return (
        <LogoUploadStep
          currentLogoUrl={null}
          onComplete={() => onComplete("logo_upload")}
          onSkip={() => onSkip("logo_upload")}
        />
      );

    case "payment_setup":
      return (
        <PaymentSetupStep
          onComplete={() => onComplete("payment_setup")}
        />
      );

    case "shipping_config":
      return (
        <ShippingConfigStep
          businessAddress=""
          onComplete={() => onComplete("shipping_config")}
        />
      );

    case "first_product":
      return (
        <FirstProductStep
          onComplete={() => onComplete("first_product")}
        />
      );

    case "services_setup":
      return (
        <ServicesSetupStep
          onComplete={() => onComplete("services_setup")}
        />
      );

    case "work_hours":
      return (
        <WorkHoursStep
          onComplete={() => onComplete("work_hours")}
        />
      );

    case "contact_form":
      return (
        <ContactFormStep
          onComplete={() => onComplete("contact_form")}
        />
      );

    case "whatsapp_connect":
      return (
        <WhatsAppConnectStep
          onComplete={() => onComplete("whatsapp_connect")}
          onSkip={() => onSkip("whatsapp_connect")}
        />
      );

    case "review_publish":
      return (
        <ReviewPublishStep
          siteType={progress.siteType}
          steps={progress.steps}
          tenantSlug={progress.tenantId}
          onPublish={() => onComplete("review_publish")}
        />
      );

    default:
      return <div className="p-4 text-muted-foreground">{"\u05E6\u05E2\u05D3 \u05DC\u05D0 \u05DE\u05D5\u05DB\u05E8"}</div>;
  }
}
